"use client"

import Box from "@mui/material/Box"
import { useTranslation } from "react-i18next"
import CurrencySelector from "@src/components/CurrencySelector"
import FAButton from "@src/components/FAButton"
import { FloatingActionsProvider, useFloatingActions } from "./context"

const DockToggle = ({ expanded }: { expanded: boolean }) => {
  const { i18n } = useTranslation()
  const { toggleDock } = useFloatingActions()
  const label = expanded
    ? i18n.language === "en"
      ? "Hide currency & contact"
      : "Ẩn tiền tệ & liên hệ"
    : i18n.language === "en"
      ? "Show currency & contact"
      : "Hiện tiền tệ & liên hệ"
  const shortLabel = expanded
    ? i18n.language === "en"
      ? "Hide"
      : "Ẩn"
    : null

  // Collapsed: vertical edge tab on the right of the viewport
  if (!expanded) {
    return (
      <Box
        component="button"
        type="button"
        data-floating-dock-toggle=""
        aria-label={label}
        aria-expanded={false}
        onClick={() => toggleDock()}
        sx={{
          all: "unset",
          boxSizing: "border-box",
          cursor: "pointer",
          position: "fixed",
          right: 0,
          bottom: { xs: "8rem", md: "10rem" },
          zIndex: 999,
          width: "2.2rem",
          height: "5.6rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#000",
          color: "#fff",
          borderRadius: "1.2rem 0 0 1.2rem",
          boxShadow: "-0.4rem 0.4rem 1.6rem rgba(0,0,0,0.18)",
          transition: "width 180ms ease, background-color 160ms ease",
          "&:hover": {
            width: "2.6rem",
            bgcolor: "#141414"
          },
          "&:focus-visible": {
            outline: "2px solid #fff",
            outlineOffset: "-3px"
          }
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 16 16"
          aria-hidden
          sx={{ width: "1.1rem", height: "1.1rem", display: "block" }}
        >
          <path
            d="M10 3.2 L5.2 8 L10 12.8"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Box>
      </Box>
    )
  }

  // Expanded: compact text chip above the dock
  return (
    <Box
      component="button"
      type="button"
      data-floating-dock-toggle=""
      aria-label={label}
      aria-expanded
      onClick={() => toggleDock()}
      sx={{
        all: "unset",
        boxSizing: "border-box",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: "0.35rem",
        px: "0.9rem",
        height: "2.4rem",
        borderRadius: "999px",
        bgcolor: "rgba(255,255,255,0.94)",
        border: "1px solid #E8E8E8",
        boxShadow: "0 0.4rem 1.2rem rgba(0,0,0,0.08)",
        color: "#6B6B6B",
        fontSize: "1.05rem",
        fontWeight: 500,
        letterSpacing: "0.04em",
        lineHeight: 1,
        transition:
          "color 160ms ease, border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease",
        "&:hover": {
          color: "#141414",
          borderColor: "#D6D6D6",
          bgcolor: "#fff",
          boxShadow: "0 0.5rem 1.4rem rgba(0,0,0,0.1)"
        },
        "&:focus-visible": {
          outline: "1.5px solid #141414",
          outlineOffset: "2px"
        }
      }}
    >
      <Box component="span">{shortLabel}</Box>
      <Box
        component="svg"
        viewBox="0 0 16 16"
        aria-hidden
        sx={{ width: "0.9rem", height: "0.9rem", display: "block" }}
      >
        <path
          d="M5.2 3.2 L10 8 L5.2 12.8"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Box>
    </Box>
  )
}

const FloatingActionsDock = () => {
  const { dockVisible } = useFloatingActions()

  if (!dockVisible) {
    return <DockToggle expanded={false} />
  }

  return (
    <Box
      sx={{
        position: "fixed",
        right: { xs: "1.6rem", md: "2.4rem" },
        bottom: { xs: "1.6rem", md: "2.4rem" },
        zIndex: 999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "0.55rem"
      }}
    >
      <DockToggle expanded />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "0.6rem",
          p: "0.6rem",
          borderRadius: "999px",
          bgcolor: "rgba(255,255,255,0.94)",
          border: "1px solid #E8E8E8",
          boxShadow: "0 1rem 2.4rem rgba(0,0,0,0.14)",
          backdropFilter: "blur(10px)"
        }}
      >
        <CurrencySelector />
        <FAButton />
      </Box>
    </Box>
  )
}

const FloatingActions = () => {
  return (
    <FloatingActionsProvider>
      <FloatingActionsDock />
    </FloatingActionsProvider>
  )
}

export default FloatingActions
